/**
 * Display formatters backed by `journalize` so match dates render in
 * Reuters house style across the app.
 *
 * `journalize` reads `Date` parts in the runtime's local timezone, so
 * the resulting strings reflect the viewer's local time.
 *
 * `formatDateMDY` stays as a tiny local helper because the calendar
 * datepicker needs a strict `"MM/DD/YYYY"` machine format.
 */

import { apdate, apmonth, aptime } from 'journalize';

import { getMatchISODate } from './iso.js';

/** AP-style short date, e.g. `"Nov. 8, 2026"`. */
export const formatDate = (iso) => apdate(new Date(iso));

/** AP-style time, e.g. `"6:30 p.m."`, `"noon"`, `"midnight"`. */
export const formatTime = (iso) => aptime(new Date(iso));

/** `"MM/DD/YYYY"` in the viewer's local timezone (datepicker format). */
export const formatDateMDY = (iso) => {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

/**
 * AP-style `{ month, day, time }` for an Opta `matchInfo`, split for
 * layout. Shape is unchanged from the previous implementation so
 * existing consumers can keep reading off `match.localDateStamp`.
 *
 * @param {{ date: string, time: string }} matchInfo
 * @returns {{ month: string, day: string, time: string }}
 */
export const getDate = (matchInfo) => {
  const d = new Date(getMatchISODate(matchInfo));
  return { month: apmonth(d), day: String(d.getDate()), time: aptime(d) };
};
